import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Sidebar from '../Components/Sidebar';
import '../Style/Payroll.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { db } from '../firebaseConfig';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc,
  doc, query, where, serverTimestamp
} from 'firebase/firestore';

import {
  FaSearch, FaPlus, FaTimes, FaEdit, FaTrashAlt,
  FaFilePdf, FaDownload, FaChevronLeft, FaChevronRight, FaEye
} from 'react-icons/fa';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import CompanyLogo from '../assets/CompanyLogo.png';

/* ---------- helpers ---------- */
const toMonthKey = (d) => {
  const dt = typeof d === 'string' ? new Date(d) : (d || new Date());
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
};
const prevMonthKey = (mk) => {
  const [y, m] = mk.split('-').map(n => parseInt(n, 10));
  const d = new Date(y, m - 2, 1);
  return toMonthKey(d);
};
const nextMonthKey = (mk) => {
  const [y, m] = mk.split('-').map(n => parseInt(n, 10));
  const d = new Date(y, m, 1);
  return toMonthKey(d);
};
const money = (v) => `R${(Number(v || 0)).toFixed(2)}`;
const debounce = (fn, wait) => { let t; return (...a)=>{ clearTimeout(t); t=setTimeout(()=>fn(...a), wait); }; };
const fmtDate = (d) => {
  if (!d) return '—';
  if (typeof d === 'string') return d.slice(0,10);
  if (d?.toDate) return d.toDate().toISOString().slice(0,10);
  if (d instanceof Date) return d.toISOString().slice(0,10);
  return '—';
};

/* ---------- AGE helpers ---------- */
const getAgeFromDate = (dob) => {
  if (!dob) return null;
  const d = dob instanceof Date ? dob : (typeof dob === 'string' ? new Date(dob) : (dob?.toDate ? dob.toDate() : null));
  if (!d || Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 && age < 130 ? age : null;
};

/* ---------- SARS 2026 (1 Mar 2025 – 28 Feb 2026) quick PAYE + UIF ---------- */
/* Brackets (annual) */
const TAX_BRACKETS_2026 = [
  { upTo: 237100, base: 0,       rate: 0.18,  threshold: 0 },
  { upTo: 370500, base: 42678,   rate: 0.26,  threshold: 237100 },
  { upTo: 512800, base: 77362,   rate: 0.31,  threshold: 370500 },
  { upTo: 673000, base: 121475,  rate: 0.36,  threshold: 512800 },
  { upTo: 857900, base: 179147,  rate: 0.39,  threshold: 673000 },
  { upTo: 1817000,base: 251258,  rate: 0.41,  threshold: 857900 },
  { upTo: Infinity,base: 644489, rate: 0.45,  threshold: 1817000 },
];
/* Rebates */
const REBATES_2026 = { primary: 17235, secondary: 9444, tertiary: 3145 };
/* UIF cap (monthly) */
const UIF_MONTHLY_CAP = 17712;           // remuneration ceiling
const UIF_EMPLOYEE_RATE = 0.01;

/** Compute estimated monthly PAYE from monthly gross and age (simple annualization).
 *  - annualTax = bracketTax(annualGross) - rebates(age)
 *  - monthlyPAYE = max(0, annualTax / 12)
 */
const computeMonthlyPAYE = (grossMonthly, age) => {
  const g = Number(grossMonthly || 0);
  if (!isFinite(g) || g <= 0) return 0;

  const annual = g * 12;
  // find bracket
  const b = TAX_BRACKETS_2026.find(br => annual <= br.upTo);
  let taxBeforeRebates;
  if (!b || b.threshold === 0) {
    // first bracket
    taxBeforeRebates = annual * 0.18;
  } else {
    taxBeforeRebates = b.base + (annual - b.threshold) * b.rate;
  }

  // rebates by age
  let rebate = REBATES_2026.primary;
  if (typeof age === 'number') {
    if (age >= 65) rebate += REBATES_2026.secondary;
    if (age >= 75) rebate += REBATES_2026.tertiary;
  }
  const annualAfterRebate = Math.max(0, taxBeforeRebates - rebate);
  return annualAfterRebate / 12;
};

/** Compute monthly UIF employee deduction from monthly gross */
const computeMonthlyUIF = (grossMonthly) => {
  const g = Number(grossMonthly || 0);
  if (!isFinite(g) || g <= 0) return 0;
  const base = Math.min(g, UIF_MONTHLY_CAP);
  return base * UIF_EMPLOYEE_RATE; // employee 1% (employer 1% not deducted from employee)
};

/* ---------- totals ---------- */
// IMPORTANT: Treat SALARY as the TOTAL GROSS monthly pay (per user instruction).
const calcTotals = (p) => {
  const e = p.earnings || {};
  const d = p.deductions || {};
  const salary       = Number(e.salary || 0); // SALARY == GROSS
  const overtime     = Number(e.overtime || 0);
  const holidayPay   = Number(e.holidayPay || 0);
  const bonus        = Number(e.bonus || 0);
  const sickLeave    = Number(e.sickLeave || 0);

  const PAYE         = Number(d.PAYE || d.paye || 0);
  const UIF          = Number(d.UIF || d.uif || 0);
  const provident    = Number(d.provident || d.provFund || 0);
  const loan         = Number(d.loan || 0);
  const cbl          = Number(d.cbl || 0);
  const sdl          = Number(d.SDL || d.sdl || 0);
  const levies       = Number(d.levies || 0);

  // SALARY is the total gross (do NOT add other earnings to gross)
  const gross = salary;

  const totalDeductions = PAYE + UIF + provident + loan + cbl + sdl + levies;
  const net = gross - totalDeductions;

  return {
    gross, totalDeductions, net,
    norm: {
      salary, overtime, holidayPay, bonus, sickLeave,
      PAYE, UIF, provident, loan, cbl, sdl, levies
    }
  };
};

/* ---------- PDF helpers (B/W only + standard header) ---------- */
const toDataUrl = async (url) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise(ok => {
      const fr = new FileReader();
      fr.onload = () => ok(fr.result);
      fr.readAsDataURL(blob);
    });
  } catch { return null; }
};

const pdfHeader = async (pdf, y = 28) => {
  const pageW = pdf.internal.pageSize.getWidth();
  const logo64 = await toDataUrl(CompanyLogo);
  const logoSize = 48;

  if (logo64) {
    pdf.addImage(logo64, undefined, (pageW - logoSize) / 2, y, logoSize, logoSize);
  }
  y += logoSize + 12;
  pdf.setFont('Helvetica', 'bold').setFontSize(15).setTextColor('#000');
  pdf.text('NYABANGA ENGINEERING', pageW / 2, y, { align: 'center' });
  y += 14;
  pdf.setFont('Helvetica', 'normal').setFontSize(8).setTextColor('#000');
  [
    'Manufacture & Repairs of Hydraulic Cylinders & Drive Train Components',
    'Plot 35, Pretoria Road, Fairlands, 1500, Benoni',
    '011 420-1537  |  082 471-9512  |  info@nyabanga.co.za',
    'Vat Reg: 4610201685  |  Reg: 200206044423'
  ].forEach(line => { pdf.text(line, pageW / 2, y, { align: 'center' }); y += 9; });
  return y + 4;
};

const bwTable = (pdf, opts) => {
  return autoTable(pdf, {
    styles: { fontSize: 8, cellPadding: 3, textColor: [0,0,0], lineColor: [0,0,0] },
    headStyles: { fillColor: [255,255,255], textColor: [0,0,0], fontSize: 8, lineColor: [0,0,0] },
    bodyStyles: { textColor: [0,0,0] },
    theme: 'grid',
    ...opts
  });
};

/* ---------- main component ---------- */
export default function Payroll() {
  const [employees, setEmployees] = useState([]);        // strictly from "users"
  const [monthKey, setMonthKey] = useState(toMonthKey(new Date()));
  const [search, setSearch] = useState('');
  const [empFilter, setEmpFilter] = useState('');        // filter by employee
  const [records, setRecords] = useState([]);            // current month records

  const [loading, setLoading] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // busy overlay
  const [working, setWorking] = useState(false);
  const [workingMsg, setWorkingMsg] = useState('Processing...');

  // create/edit form state
  const [showForm, setShowForm] = useState(false);
  const emptyForm = {
    id: null,
    userId: '',
    userName: '',
    userEmail: '',
    userAge: null,             // <-- derived from user's birthday
    notes: '',
    earnings: { salary: '', overtime: '', holidayPay: '', bonus: '', sickLeave: '' },
    deductions: { PAYE: '', UIF: '', provident: '', loan: '', cbl: '', sdl: '', levies: '' }
  };
  const [form, setForm] = useState(emptyForm);

  // track if user manually edited PAYE/UIF so we don't overwrite their overrides
  const [payeTouched, setPayeTouched] = useState(false);
  const [uifTouched, setUifTouched] = useState(false);

  // view modal state
  const [view, setView] = useState({ open: false, rec: null });

  // confirm dialog state
  const [confirmDlg, setConfirmDlg] = useState({ open: false, type: '', rec: null, meta: {} });

  const [exporting, setExporting] = useState(false);

  const handleSearch = useCallback(debounce((val) => setSearch(val), 250), []);

  /* ---------- fetch employees (users only) ---------- */
  useEffect(() => {
    (async () => {
      setLoadingUsers(true);
      try {
        const uSnap = await getDocs(collection(db, 'users'));
        const arr = uSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setEmployees(arr.map(u => {
          // birthday can be Firestore Timestamp, string, or Date
          const rawDob = u.birthday?.toDate ? u.birthday.toDate() : (u.birthday || null);
          const age = getAgeFromDate(rawDob);
          return {
            id: u.id,
            name: u.firstName || u.fullName || u.displayName || u.email || 'Unnamed',
            email: u.email || '',
            birthday: rawDob,
            age
          };
        }).sort((a,b)=>a.name.localeCompare(b.name)));
      } catch (e) {
        console.error(e);
        toast.error('Failed to load users.');
      } finally {
        setLoadingUsers(false);
      }
    })();
  }, []);

  /* ---------- fetch payrolls for current month ---------- */
  const fetchMonth = useCallback(async (mk) => {
    setLoading(true);
    try {
      const q1 = query(collection(db, 'Payroll'), where('month', '==', mk));
      const snap = await getDocs(q1);
      const arr = snap.docs.map(d => ({ docId: d.id, ...d.data() }))
        .sort((a,b) => (a.userName || '').localeCompare(b.userName || ''));
      setRecords(arr);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load payroll records.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMonth(monthKey); }, [monthKey, fetchMonth]);

  /* ---------- derived filtered list ---------- */
  const filtered = useMemo(() => {
    let list = records;
    if (empFilter) list = list.filter(r => r.userId === empFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(r => JSON.stringify(r).toLowerCase().includes(s));
    }
    return list;
  }, [records, search, empFilter]);

  /* ---------- form helpers ---------- */
  const openCreate = () => { 
    setForm(emptyForm); 
    setPayeTouched(false); 
    setUifTouched(false);
    setShowForm(true); 
  };

  const onPickEmployee = (id) => {
    const u = employees.find(e => e.id === id);
    const age = u?.age ?? null;
    setForm(f => ({
      ...f,
      userId: id,
      userName: u?.name || '',
      userEmail: u?.email || '',
      userAge: age
    }));
  };

  const setEarn = (k, v) =>
    setForm(f => ({ ...f, earnings: { ...f.earnings, [k]: v } }));
  const setDed  = (k, v) =>
    setForm(f => ({ ...f, deductions: { ...f.deductions, [k]: v } }));

  const currentTotals = useMemo(() => calcTotals(form), [form]);

  /* ---------- AUTO: compute PAYE + UIF whenever SALARY or employee age changes ---------- */
  useEffect(() => {
    const salaryForCalc = Number(form.earnings?.salary || 0);
    const age = form.userAge;
    if (salaryForCalc > 0) {
      const paye = computeMonthlyPAYE(salaryForCalc, age);
      const uif  = computeMonthlyUIF(salaryForCalc);
      // only auto-apply if user hasn't overridden those fields in this session
      if (!payeTouched) setDed('PAYE', paye.toFixed(2));
      if (!uifTouched)  setDed('UIF',  uif.toFixed(2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.earnings?.salary, form.userAge]);

  /* ---------- normalize + build payload ---------- */
  const buildPayload = () => {
    const normalizeNums = obj =>
      Object.keys(obj).reduce((acc, k) => ({ ...acc, [k]: Number(obj[k] || 0) }), {});

    return {
      month: monthKey,
      userId: form.userId,
      userName: form.userName,
      userEmail: form.userEmail,
      earnings: normalizeNums(form.earnings),
      deductions: {
        PAYE: Number(form.deductions.PAYE || form.deductions.paye || 0),
        UIF: Number(form.deductions.UIF || form.deductions.uif || 0),
        provident: Number(form.deductions.provident || form.deductions.provFund || 0),
        loan: Number(form.deductions.loan || 0),
        cbl: Number(form.deductions.cbl || 0),
        sdl: Number(form.deductions.sdl || form.deductions.SDL || 0),
        levies: Number(form.deductions.levies || 0)
      },
      notes: form.notes || '',
      updatedAt: serverTimestamp(),
    };
  };

  /* ---------- save (create/update) with pre-confirmation ---------- */
  const onSubmitForm = (e) => {
    e.preventDefault();
    if (!form.userId) { toast.error('Select an employee.'); return; }

    // Ensure SALARY (gross) sensible if provided
    if (form.earnings?.salary && Number(form.earnings.salary) < 0) {
      toast.error('Enter a valid Salary (gross monthly).');
      return;
    }

    const payload = buildPayload();

    // If creating, block duplicate per user per month
    if (!form.id) {
      const existing = records.find(r => r.userId === payload.userId);
      if (existing) { toast.error('Payroll for this user in the selected month already exists. Edit it instead.'); return; }
    }

    // Open confirmation dialog before committing save
    setConfirmDlg({
      open: true,
      type: 'save',
      rec: payload,
      meta: { isUpdate: !!form.id, docId: form.id }
    });
  };

  const doSave = async (payload, isUpdate, docId) => {
    try {
      setWorkingMsg(isUpdate ? 'Updating payroll...' : 'Saving payroll...');
      setWorking(true);
      if (!isUpdate) {
        const ref = await addDoc(collection(db, 'Payroll'), { ...payload, createdAt: serverTimestamp() });
        setRecords(prev => [...prev, { docId: ref.id, ...payload }]
          .sort((a,b)=> (a.userName||'').localeCompare(b.userName||'')));
        toast.success('Payroll created.');
      } else {
        await updateDoc(doc(db, 'Payroll', docId), payload);
        setRecords(prev => prev.map(r => r.docId === docId ? { ...r, ...payload } : r));
        toast.success('Payroll updated.');
      }
      setShowForm(false);
    } catch (err) {
      console.error(err);
      toast.error('Save failed.');
    } finally {
      setWorking(false);
    }
  };

  /* ---------- open edit with confirmation ---------- */
  const requestEdit = (rec) => setConfirmDlg({ open: true, type: 'edit', rec, meta: {} });

  const confirmProceed = () => {
    const { type, rec, meta } = confirmDlg;
    setConfirmDlg({ open: false, type: '', rec: null, meta: {} });

    if (type === 'edit' && rec) {
      setForm({
        id: rec.docId,
        userId: rec.userId,
        userName: rec.userName,
        userEmail: rec.userEmail,
        userAge: null, // will refresh on select below if needed
        notes: rec.notes || '',
        earnings: {
          salary: rec.earnings?.salary ?? '',
          overtime: rec.earnings?.overtime ?? '',
          holidayPay: rec.earnings?.holidayPay ?? '',
          bonus: rec.earnings?.bonus ?? '',
          sickLeave: rec.earnings?.sickLeave ?? '',
        },
        deductions: {
          PAYE: rec.deductions?.PAYE ?? '',
          UIF: rec.deductions?.UIF ?? '',
          provident: rec.deductions?.provident ?? '',
          loan: rec.deductions?.loan ?? '',
          cbl: rec.deductions?.cbl ?? '',
          sdl: rec.deductions?.sdl ?? '',
          levies: rec.deductions?.levies ?? '',
        }
      });
      setPayeTouched(false);
      setUifTouched(false);
      setShowForm(true);

      // If we know the employee from the list, set age now
      const u = employees.find(e => e.id === rec.userId);
      if (u) setForm(f => ({ ...f, userAge: u.age ?? null }));
    }

    if (type === 'delete' && rec) removeRecord(rec.docId);

    if (type === 'save' && rec) {
      doSave(rec, !!meta.isUpdate, meta.docId);
    }
  };
  const cancelConfirm = () => setConfirmDlg({ open: false, type: '', rec: null, meta: {} });

  /* ---------- delete (after confirmation) ---------- */
  const requestDelete = (rec) => setConfirmDlg({ open: true, type: 'delete', rec, meta: {} });

  const removeRecord = async (id) => {
    try {
      setWorkingMsg('Deleting payroll...');
      setWorking(true);
      await deleteDoc(doc(db, 'Payroll', id));
      setRecords(prev => prev.filter(r => r.docId !== id));
      toast.success('Deleted.');
    } catch (e) {
      toast.error('Delete failed.');
    } finally {
      setWorking(false);
    }
  };

  /* ---------- view modal ---------- */
  const openView = (rec) => setView({ open: true, rec });
  const closeView = () => setView({ open: false, rec: null });

  /* ---------- per-user PDF (payslip) ---------- */
  const generatePayslipPdf = async (rec) => {
    try {
      setWorkingMsg('Generating payslip PDF...');
      setWorking(true);

      const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
      let y = await pdfHeader(pdf, 28);

      pdf.setFont('Helvetica', 'bold').setTextColor('#000').setFontSize(12);
      pdf.text('PAYSLIP', 40, y);
      y += 12;

      bwTable(pdf, {
        startY: y,
        head: [['Employee', 'Email', 'Month']],
        body: [[rec.userName || '—', rec.userEmail || '—', rec.month]],
      });
      y = pdf.lastAutoTable.finalY + 8;

      const t = calcTotals(rec);
      bwTable(pdf, {
        startY: y,
        head: [['EARNINGS', 'Amount']],
        body: [
          ['Salary (Gross)', money(t.norm.salary)],
          ['Overtime', money(t.norm.overtime)],
          ['Holiday Pay', money(t.norm.holidayPay)],
          ['Bonus', money(t.norm.bonus)],
          ['Sick Leave', money(t.norm.sickLeave)],
          ['GROSS EARNINGS', money(t.gross)]
        ],
      });
      y = pdf.lastAutoTable.finalY + 6;

      bwTable(pdf, {
        startY: y,
        head: [['DEDUCTIONS', 'Amount']],
        body: [
          ['PAYE', money(t.norm.PAYE)],
          ['UIF', money(t.norm.UIF)],
          ['Provident Fund', money(t.norm.provident)],
          ['Loan', money(t.norm.loan)],
          ['CBL', money(t.norm.cbl)],
          ['SDL', money(t.norm.sdl)],
          ['Levies', money(t.norm.levies)],
          ['TOTAL DEDUCTIONS', money(t.totalDeductions)]
        ],
      });
      y = pdf.lastAutoTable.finalY + 10;

      pdf.setFont('Helvetica', 'bold').setFontSize(12);
      pdf.text(`NET PAY: ${money(t.net)}`, 40, y);
      y += 14;

      if (rec.notes) {
        pdf.setFont('Helvetica', 'normal').setFontSize(9);
        pdf.text(`Notes: ${rec.notes}`, 40, y);
      }

      pdf.save(`Payslip_${rec.month}_${(rec.userName || 'Employee').replace(/\s+/g,'_')}.pdf`);
      toast.success('Payslip generated.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate payslip.');
    } finally {
      setWorking(false);
    }
  };

  /* ---------- overall export (CSV + PDF) ---------- */
  const exportAll = async () => {
    if (!records.length) { toast.info('No records to export.'); return; }
    setExporting(true);
    setWorking(true);
    setWorkingMsg('Exporting CSV and PDF...');

    try {
      // CSV
      const headers = [
        'Employee','Email','Month','Gross',
        'Salary','Overtime','HolidayPay','Bonus','SickLeave',
        'PAYE','UIF','Provident','Loan','CBL','SDL','Levies','Deductions','Net','Notes'
      ];
      const rows = records.map(r => {
        const t = calcTotals(r);
        return [
          r.userName || '', r.userEmail || '', r.month, t.gross,
          t.norm.salary, t.norm.overtime, t.norm.holidayPay, t.norm.bonus, t.norm.sickLeave,
          t.norm.PAYE, t.norm.UIF, t.norm.provident, t.norm.loan, t.norm.cbl, t.norm.sdl, t.norm.levies,
          t.totalDeductions, t.net,
          (r.notes || '').replace(/\r?\n/g, ' ')
        ];
      });
      const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `Payroll_${monthKey}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // PDF summary
      const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
      let y = await pdfHeader(pdf, 28);

      pdf.setFont('Helvetica', 'bold').setFontSize(11).setTextColor('#000');
      pdf.text(`PAYROLL SUMMARY — ${monthKey}`, 40, y);
      y += 10;

      const head = [[
        'Emp','Gross','Sal','OT','Hol','Bon','Sick','PAYE','UIF','Prov','Loan','CBL','SDL','Lev','Ded','Net'
      ]];
      const body = records.map(r => {
        const t = calcTotals(r);
        return [
          r.userName || '',
          money(t.gross),
          money(t.norm.salary),
          money(t.norm.overtime),
          money(t.norm.holidayPay),
          money(t.norm.bonus),
          money(t.norm.sickLeave),
          money(t.norm.PAYE),
          money(t.norm.UIF),
          money(t.norm.provident),
          money(t.norm.loan),
          money(t.norm.cbl),
          money(t.norm.sdl),
          money(t.norm.levies),
          money(t.totalDeductions),
          money(t.net)
        ];
      });

      bwTable(pdf, {
        startY: y,
        head,
        body,
        columnStyles: {
          0: { cellWidth: 110 },
        },
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fontSize: 8 }
      });

      pdf.save(`Payroll_${monthKey}.pdf`);
      toast.success('Export complete.');
    } catch (err) {
      console.error(err);
      toast.error('Export failed.');
    } finally {
      setExporting(false);
      setWorking(false);
    }
  };

  // Pretty month, e.g. "August 2025" from "2025-08"
  const fmtMonthLong = (mk) => {
    if (!mk) return '—';
    const [y, m] = String(mk).split('-').map(n => parseInt(n, 10));
    if (!y || !m) return mk;
    return new Date(y, m - 1, 1).toLocaleString('en-ZA', { month: 'long', year: 'numeric' });
  };

  /* ---------- UI ---------- */
  return (
    <div className="payroll-page">
      <ToastContainer position="top-right" autoClose={2200} />
      <Sidebar />

      <main className="payroll-main" aria-busy={loading || loadingUsers}>
        <div className="title-row">
          <div className="title-left">
            <h2>Payroll</h2>
            <span className="badge-month">{fmtMonthLong(monthKey)}</span>
          </div>
          <div className="month-switch">
            <button className="btn-icon" onClick={() => setMonthKey(prevMonthKey(monthKey))} aria-label="Previous month">
              <FaChevronLeft />
            </button>
            <input
              type="month"
              value={monthKey}
              onChange={e => setMonthKey(e.target.value)}
              className="month-input"
            />
            <button className="btn-icon" onClick={() => setMonthKey(nextMonthKey(monthKey))} aria-label="Next month">
              <FaChevronRight />
            </button>
          </div>
        </div>

        {/* Top controls */}
        <div className="top-bar">
          <div className="search-wrap">
            <FaSearch className="icon" />
            <input
              className="search-input"
              placeholder="Search payroll…"
              onChange={e => handleSearch(e.target.value)}
            />
          </div>
          <div className="filters">
            <select
              value={empFilter}
              onChange={e => setEmpFilter(e.target.value)}
              className="emp-filter"
              title="Filter by employee"
              disabled={loadingUsers}
            >
              <option value="">{loadingUsers ? 'Loading employees…' : 'All Employees'}</option>
              {employees.map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div className="top-actions">
            <button className="btn-outline" onClick={exportAll} disabled={exporting || loading}>
              {exporting ? <span className="spinner" aria-hidden="true" /> : <FaDownload />}
              <span>{exporting ? 'Exporting…' : 'Export CSV + PDF'}</span>
            </button>
            <button className="btn-primary" onClick={openCreate} disabled={loading || loadingUsers}>
              <FaPlus /> <span>New Payroll</span>
            </button>
          </div>
        </div>

        {/* Form */}
        {showForm && (
          <form className="card payroll-form" onSubmit={onSubmitForm}>
            <div className="form-head">
              <div className="form-title">
                <h3>{form.id ? 'Edit Payroll' : 'Create Payroll'}</h3>
                <div className="form-sub">Salary is treated as <b>total gross</b>. PAYE & UIF auto-calc from Salary (editable).</div>
              </div>
              <button type="button" className="btn-icon" onClick={() => setShowForm(false)} aria-label="Close form">
                <FaTimes />
              </button>
            </div>

            <div className="grid-4">
              <div className="field">
                <label>Employee <span className="required">*</span></label>
                <select
                  value={form.userId}
                  onChange={e => onPickEmployee(e.target.value)}
                  disabled={loadingUsers}
                >
                  <option value="">{loadingUsers ? 'Loading…' : '— Select —'}</option>
                  {employees.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.name}{u.email ? ` — ${u.email}` : ''}{typeof u.age === 'number' ? ` — Age ${u.age}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>Month</label>
                <input type="month" value={monthKey} onChange={e => setMonthKey(e.target.value)} />
              </div>

              <div className="field">
                <label>Email</label>
                <input value={form.userEmail} onChange={e => setForm(f=>({...f, userEmail: e.target.value}))} placeholder="optional" />
              </div>

              <div className="field">
                <label>Notes</label>
                <input value={form.notes} onChange={e => setForm(f=>({...f, notes: e.target.value}))} placeholder="e.g., incl. 5h OT on Sat" />
              </div>
            </div>

            {/* STATUTORY CALC */}
            <h4 className="sub-title">Statutory (Auto)</h4>
            <div className="grid-3">
              <div className="field">
                <label>Age (from user)</label>
                <input value={typeof form.userAge === 'number' ? form.userAge : '—'} disabled />
                <div className="hint">{typeof form.userAge === 'number' ? 'Age read from birthday' : 'No birthday on profile'}</div>
              </div>

              <div className="field">
                <label>PAYE (auto, editable)</label>
                <input
                  type="number" min="0" step="0.01"
                  value={form.deductions.PAYE}
                  onChange={e => { setDed('PAYE', e.target.value); setPayeTouched(true); }}
                />
                <div className="hint">Calculated from SARS 2026 brackets & rebates using Salary; you can override.</div>
              </div>

              <div className="field">
                <label>UIF (auto, editable)</label>
                <input
                  type="number" min="0" step="0.01"
                  value={form.deductions.UIF}
                  onChange={e => { setDed('UIF', e.target.value); setUifTouched(true); }}
                />
                <div className="hint">1% of Salary up to R17,712/month; you can override.</div>
              </div>
            </div>

            <h4 className="sub-title">Earnings</h4>
            <div className="grid-5">
              <div className="field"><label>Salary (Total Gross)</label>
                <input type="number" min="0" step="0.01" value={form.earnings.salary} onChange={e=>setEarn('salary', e.target.value)} />
                <div className="hint">This value is treated as the total gross for calculations.</div>
              </div>
              <div className="field"><label>Overtime</label>
                <input type="number" min="0" step="0.01" value={form.earnings.overtime} onChange={e=>setEarn('overtime', e.target.value)} />
              </div>
              <div className="field"><label>Holiday Pay</label>
                <input type="number" min="0" step="0.01" value={form.earnings.holidayPay} onChange={e=>setEarn('holidayPay', e.target.value)} />
              </div>
              <div className="field"><label>Bonus</label>
                <input type="number" min="0" step="0.01" value={form.earnings.bonus} onChange={e=>setEarn('bonus', e.target.value)} />
              </div>
              <div className="field"><label>Sick Leave</label>
                <input type="number" min="0" step="0.01" value={form.earnings.sickLeave} onChange={e=>setEarn('sickLeave', e.target.value)} />
              </div>
            </div>

            <h4 className="sub-title">Deductions</h4>
            <div className="grid-7">
              <div className="field"><label>PAYE</label>
                <input type="number" min="0" step="0.01" value={form.deductions.PAYE} onChange={e=>{ setDed('PAYE', e.target.value); setPayeTouched(true); }} />
              </div>
              <div className="field"><label>UIF</label>
                <input type="number" min="0" step="0.01" value={form.deductions.UIF} onChange={e=>{ setDed('UIF', e.target.value); setUifTouched(true); }} />
              </div>
              <div className="field"><label>Provident Fund</label>
                <input type="number" min="0" step="0.01" value={form.deductions.provident} onChange={e=>setDed('provident', e.target.value)} />
              </div>
              <div className="field"><label>Loan</label>
                <input type="number" min="0" step="0.01" value={form.deductions.loan} onChange={e=>setDed('loan', e.target.value)} />
              </div>
              <div className="field"><label>CBL</label>
                <input type="number" min="0" step="0.01" value={form.deductions.cbl} onChange={e=>setDed('cbl', e.target.value)} />
              </div>
              <div className="field"><label>SDL</label>
                <input type="number" min="0" step="0.01" value={form.deductions.sdl} onChange={e=>setDed('sdl', e.target.value)} />
              </div>
              <div className="field"><label>Levies</label>
                <input type="number" min="0" step="0.01" value={form.deductions.levies} onChange={e=>setDed('levies', e.target.value)} />
              </div>
            </div>

            <div className="totals-row">
              <div className="total-chip gross">Gross: {money(currentTotals.gross)}</div>
              <div className="total-chip deduct">Deductions: {money(currentTotals.totalDeductions)}</div>
              <div className="total-chip net">Net Pay: {money(currentTotals.net)}</div>
            </div>

            <div className="actions-row">
              <button type="submit" className="btn-primary">
                <span className="btn-inner">
                  <span className="btn-label">{form.id ? 'Confirm Update…' : 'Confirm & Save…'}</span>
                </span>
              </button>
              <button type="button" className="btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </form>
        )}

        {/* Table (basic info) */}
        <div className="card table-card">
          {loading ? (
            <div className="loading">
              <span className="spinner lg" aria-hidden="true" />
              <div>Loading payroll…</div>
            </div>
          ) : filtered.length ? (
            <div className="table-wrap">
              <table className="pay-table compact">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Salary</th>
                    <th>Gross</th>
                    <th>Deductions</th>
                    <th>Net</th>
                    <th className="actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(rec => {
                    const t = calcTotals(rec);
                    return (
                      <tr key={rec.docId}>
                        <td>
                          <div className="emp">
                            <div className="emp-name">{rec.userName || '—'}</div>
                            <div className="emp-email">{rec.userEmail || ''}</div>
                          </div>
                        </td>
                        <td>{money(t.norm.salary)}</td>
                        <td>{money(t.gross)}</td>
                        <td>{money(t.totalDeductions)}</td>
                        <td><b>{money(t.net)}</b></td>
                        <td className="actions">
                          <button className="btn-icon" title="View" onClick={() => openView(rec)}><FaEye /></button>
                          <button className="btn-icon" title="Payslip PDF" onClick={() => generatePayslipPdf(rec)}><FaFilePdf /></button>
                          <button className="btn-icon" title="Edit" onClick={() => requestEdit(rec)}><FaEdit /></button>
                          <button className="btn-icon danger" title="Delete" onClick={() => requestDelete(rec)}><FaTrashAlt /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-results">No payroll records for {fmtMonthLong(monthKey)} match your filters.</div>
          )}
        </div>
      </main>

      {/* Confirm Dialog */}
      {confirmDlg.open && (
        <div className="dialog-overlay" role="dialog" aria-modal="true">
          <div className="dialog">
            {confirmDlg.type === 'save' ? (
              <>
                <h3>Confirm {confirmDlg.meta?.isUpdate ? 'Update' : 'Save'} Payroll</h3>
                <div className="confirm-grid">
                  <div><b>Employee:</b> {form.userName || '—'}</div>
                  <div><b>Month:</b> {monthKey}</div>
                  <div className="full">
                    {(() => {
                      const t = calcTotals({ earnings: confirmDlg.rec.earnings, deductions: confirmDlg.rec.deductions });
                      return (
                        <div className="confirm-totals">
                          <div className="mini-card">Gross: <b>{money(t.gross)}</b></div>
                          <div className="mini-card">Deductions: <b>{money(t.totalDeductions)}</b></div>
                          <div className="mini-card highlight">Net: <b>{money(t.net)}</b></div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <div className="dialog-actions">
                  <button className="btn-cancel" onClick={cancelConfirm}>Cancel</button>
                  <button className="btn-confirm" onClick={confirmProceed}>
                    {confirmDlg.meta?.isUpdate ? 'Update' : 'Save'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3>{confirmDlg.type === 'delete' ? 'Confirm Delete' : 'Confirm Edit'}</h3>
                <p>
                  {confirmDlg.type === 'delete'
                    ? `Delete payroll for ${confirmDlg.rec?.userName} (${confirmDlg.rec?.month})?`
                    : `Edit payroll for ${confirmDlg.rec?.userName} (${confirmDlg.rec?.month})?`}
                </p>
                <div className="dialog-actions">
                  <button className="btn-cancel" onClick={cancelConfirm}>Cancel</button>
                  <button
                    className={confirmDlg.type === 'delete' ? 'btn-delete' : 'btn-confirm'}
                    onClick={confirmProceed}
                  >
                    {confirmDlg.type === 'delete' ? 'Delete' : 'Edit'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* View Modal (all info) */}
      {view.open && view.rec && (
        <div className="dialog-overlay" role="dialog" aria-modal="true">
          <div className="dialog view-dialog">
            <div className="dialog-head">
              <div className="dialog-title">
                <h3>Payroll Details</h3>
                <span className="badge">{view.rec.month}</span>
              </div>
              <button className="btn-icon" onClick={closeView} aria-label="Close"><FaTimes /></button>
            </div>

            <div className="view-sections">
              <section className="section">
                <h4 className="section-title">Employee</h4>
                <div className="info-grid">
                  <div><b>Name:</b> {view.rec.userName || '—'}</div>
                  <div><b>Email:</b> {view.rec.userEmail || '—'}</div>
                  <div><b>Month:</b> {view.rec.month}</div>
                  {view.rec.createdAt && <div><b>Created:</b> {fmtDate(view.rec.createdAt)}</div>}
                  {view.rec.updatedAt && <div><b>Updated:</b> {fmtDate(view.rec.updatedAt)}</div>}
                  {view.rec.notes && <div className="full"><b>Notes:</b> {view.rec.notes}</div>}
                </div>
              </section>

              <section className="section">
                <h4 className="section-title">Earnings</h4>
                <div className="info-grid">
                  <div><b>Salary (Gross):</b> {money(view.rec.earnings?.salary)}</div>
                  <div><b>Overtime:</b> {money(view.rec.earnings?.overtime)}</div>
                  <div><b>Holiday Pay:</b> {money(view.rec.earnings?.holidayPay)}</div>
                  <div><b>Bonus:</b> {money(view.rec.earnings?.bonus)}</div>
                  <div><b>Sick Leave:</b> {money(view.rec.earnings?.sickLeave)}</div>
                </div>
              </section>

              <section className="section">
                <h4 className="section-title">Deductions</h4>
                <div className="info-grid">
                  <div><b>PAYE:</b> {money(view.rec.deductions?.PAYE)}</div>
                  <div><b>UIF:</b> {money(view.rec.deductions?.UIF)}</div>
                  <div><b>Provident:</b> {money(view.rec.deductions?.provident)}</div>
                  <div><b>Loan:</b> {money(view.rec.deductions?.loan)}</div>
                  <div><b>CBL:</b> {money(view.rec.deductions?.cbl)}</div>
                  <div><b>SDL:</b> {money(view.rec.deductions?.sdl)}</div>
                  <div><b>Levies:</b> {money(view.rec.deductions?.levies)}</div>
                </div>
              </section>

              <section className="section">
                <h4 className="section-title">Totals</h4>
                {(() => {
                  const t = calcTotals(view.rec);
                  return (
                    <div className="totals-cards">
                      <div className="stat-card gross">
                        <div className="stat-label">Gross</div>
                        <div className="stat-value">{money(t.gross)}</div>
                      </div>
                      <div className="stat-card deduct">
                        <div className="stat-label">Deductions</div>
                        <div className="stat-value">{money(t.totalDeductions)}</div>
                      </div>
                      <div className="stat-card net">
                        <div className="stat-label">Net Pay</div>
                        <div className="stat-value">{money(t.net)}</div>
                      </div>
                    </div>
                  );
                })()}
              </section>
            </div>

            <div className="dialog-actions sticky-actions">
              <button className="btn-secondary" onClick={() => generatePayslipPdf(view.rec)}>
                <FaFilePdf /> Payslip PDF
              </button>
              <button className="btn-cancel" onClick={closeView}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Global working overlay */}
      {working && (
        <div className="processing-overlay" aria-live="assertive">
          <div className="processing-card">
            <span className="spinner xl" aria-hidden="true" />
            <div className="processing-text">{workingMsg}</div>
          </div>
        </div>
      )}
    </div>
  );
}
